/* eslint-disable warp-drive/no-legacy-request-patterns */
import SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import type Subject from '@linc-technologies/linced-common/models/goals/subject';
import type Page from '@linc-technologies/linced-common/models/posts/page';
import type Store from '@linc-technologies/linced-common/services/store';
import { filterBy } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';

// GoalSubject is the part of a saved goals/subject record these helpers read.
// school_id is an alias of school.id. It is empty when the school is not loaded.
type GoalSubject = Pick<Subject, 'school_id' | 'reportVisibility'> & {
  id: string;
};

// ReportPage is the part of a posts/page record getVisibleSubjects reads.
// Its subjects are saved, so their ids are not null.
type ReportPage = {
  goalSubject_ids: readonly NonNullable<Page['goalSubject_ids'][number]>[];
};

// ReportRoute is the part of a route getSubjectData fetches through. The
// callers are the person and learner routes in linced-app and linced-parents.
interface ReportRoute {
  // TS-STANDIN(service:hero-api): only goals.getLearnerSummary.
  heroApi: {
    goals: {
      getLearnerSummary(
        schoolId: string,
        personId: string,
        subjectId: string,
        asAtTimeMillis: number | null,
      ): Promise<unknown>;
    };
  };
  store: Store;
}

// SubjectData holds a promise per kind of subject data, each resolving to an
// object keyed by subject id. The routes await them (through hash) before
// the page renders.
interface SubjectData {
  subjectSummaries: Promise<Record<string, unknown>>;
  expectations: Promise<Record<string, unknown>>;
  progressions: Promise<Record<string, unknown>>;
}

export function getSubjectData(
  route: ReportRoute,
  personId: string,
  subjects: readonly GoalSubject[],
  endTime?: number,
) {
  // Filled in below.
  const promises = {} as SubjectData;

  // Filter out any subjects which haven't been loaded.  These are likely to be
  // unavailable to the user (i.e. not visible to teachers).
  const filteredSubjects = filterBy(subjects, 'school_id');

  // Fetch subject-specific data for those subjects which will be visible.
  promises.subjectSummaries = hash(
    filteredSubjects.reduce(
      (summaries, subject) => {
        summaries[subject.id] = route.heroApi.goals.getLearnerSummary(
          // filterBy removes the subjects that have no school_id.
          subject.school_id as string,
          personId,
          subject.id,
          endTime ? endTime * 1000 : null,
        );
        return summaries;
      },
      {} as Record<string, Promise<unknown>>,
    ),
  );

  promises.expectations = hash(
    filteredSubjects.reduce(
      (expectations, subject) => {
        expectations[subject.id] = route.store.query('milestones/expectation', {
          schoolId: subject.school_id,
          goalSubjectId: subject.id,
        });
        return expectations;
      },
      {} as Record<string, Promise<unknown>>,
    ),
  );

  const progressionFilters: {
    personId: string;
    latest: boolean;
    asAtTime?: number;
  } = {
    personId,
    latest: true,
  };
  if (endTime) {
    progressionFilters.asAtTime = endTime;
  }

  promises.progressions = hash(
    filteredSubjects.reduce(
      (progressions, subject) => {
        // We can't reuse `progressionFilters` as that results in all requests
        // containing the same (last set of) params.  Copy the base filters onto
        // a new object for this specific subject.
        const subjectFilters = {
          schoolId: subject.school_id,
          goalSubjectId: subject.id,
        };
        Object.assign(subjectFilters, progressionFilters);
        progressions[subject.id] = route.store.query(
          'milestones/progression',
          subjectFilters,
        );
        return progressions;
      },
      {} as Record<string, Promise<unknown>>,
    ),
  );

  return promises;
}

export function getVisibleSubjects<T extends GoalSubject>(
  allSubjects: readonly T[],
  pages: readonly ReportPage[],
): T[] {
  const assignedSubjectIds = pages.reduce<string[]>((ids, page) => {
    // TS-NARROWED: goalSubject_ids is always an array, so `|| []` never runs.
    return ids.concat(page.goalSubject_ids || []);
  }, []);

  return allSubjects.filter((subject) => {
    return (
      subject.reportVisibility === SubjectReportVisibility.ALWAYS ||
      (subject.reportVisibility === SubjectReportVisibility.IF_PAGE_ON_REPORT &&
        assignedSubjectIds.includes(subject.id))
    );
  });
}
